const FeedbackIntelligence = () => {
  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">
              Feedback Intelligence Report
              </h1>
              <p className="text-sm text-gray-600">
              AI Dashboard for Swachh Bharat, Health & Amenities Command Team
              </p>
            </div>
          </div>
        </div>

        {/* Top Row - Category Distribution & Sentiment Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Category Distribution Card */}
          <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-foreground mb-1">
                Category Distribution
              </h2>
              <p className="text-xs text-muted-foreground">
                AI Categorization of 62 feedbacks (last 24 hrs)
              </p>
            </div>

            {/* Pie Chart */}
            <div className="flex items-center justify-center mb-6 relative">
              <svg width="280" height="280" viewBox="0 0 280 280" className="transform -rotate-90">
                {/* Cleanliness 40% - Purple */}
                <circle
                  cx="140"
                  cy="140"
                  r="100"
                  fill="none"
                  stroke="hsl(248, 70%, 65%)"
                  strokeWidth="80"
                  strokeDasharray="251.2 628"
                  strokeDashoffset="0"
                />
                {/* Electricity 15% - Darker Purple */}
                <circle
                  cx="140"
                  cy="140"
                  r="100"
                  fill="none"
                  stroke="hsl(260, 60%, 55%)"
                  strokeWidth="80"
                  strokeDasharray="94.2 628"
                  strokeDashoffset="-251.2"
                />
                {/* Queue/Overcrowding 20% - Medium Purple */}
                <circle
                  cx="140"
                  cy="140"
                  r="100"
                  fill="none"
                  stroke="hsl(254, 65%, 60%)"
                  strokeWidth="80"
                  strokeDasharray="125.6 628"
                  strokeDashoffset="-345.4"
                />
                {/* Water 25% - Light Purple/Blue */}
                <circle
                  cx="140"
                  cy="140"
                  r="100"
                  fill="none"
                  stroke="hsl(220, 80%, 70%)"
                  strokeWidth="80"
                  strokeDasharray="157 628"
                  strokeDashoffset="-471"
                />
              </svg>
              
              {/* Labels */}
              <div className="absolute top-12 right-8 text-sm">
                <span className="text-primary">Cleanliness 40%</span>
              </div>
              <div className="absolute bottom-32 left-4 text-sm">
                <span className="text-[hsl(220,80%,70%)]">Water 25%</span>
              </div>
              <div className="absolute bottom-16 right-6 text-sm">
                <span className="text-[hsl(254,65%,60%)]">Queue/Overcrowding 20%</span>
              </div>
              <div className="absolute bottom-32 left-20 text-sm">
                <span className="text-[hsl(260,60%,55%)]">Electricity 15%</span>
              </div>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-4 justify-center mb-3">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-primary"></div>
                <span className="text-xs text-muted-foreground">Cleanliness</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[hsl(260,60%,55%)]"></div>
                <span className="text-xs text-muted-foreground">Electricity</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[hsl(254,65%,60%)]"></div>
                <span className="text-xs text-muted-foreground">Queue/Overcrowding</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[hsl(220,80%,70%)]"></div>
                <span className="text-xs text-muted-foreground">Water</span>
              </div>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              Based on 62 total feedbacks from pilgrims via QR & mobile inputs
            </p>
          </div>

          {/* Sentiment Summary Card */}
          <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-foreground mb-1">
                Sentiment Summary
              </h2>
              <p className="text-xs text-muted-foreground">
                Feedback sentiment analysis with trends
              </p>
            </div>

            {/* Table */}
            <div className="mb-6">
              <div className="grid grid-cols-3 gap-4 pb-3 border-b border-border text-xs font-medium text-muted-foreground">
                <div>Sentiment</div>
                <div className="text-center">Volume</div>
                <div className="text-right">Trend</div>
              </div>

              {/* Positive */}
              <div className="grid grid-cols-3 gap-4 py-3 border-b border-border items-center">
                <div className="flex items-center gap-2">
                  <span className="text-base">😊</span>
                  <span className="text-sm text-foreground">Positive</span>
                </div>
                <div className="text-center text-sm font-semibold text-foreground">22</div>
                <div className="text-right text-sm text-success flex items-center justify-end gap-1">
                  <span>↗</span>
                  <span>Increasing</span>
                </div>
              </div>

              {/* Neutral */}
              <div className="grid grid-cols-3 gap-4 py-3 border-b border-border items-center">
                <div className="flex items-center gap-2">
                  <span className="text-base">😐</span>
                  <span className="text-sm text-foreground">Neutral</span>
                </div>
                <div className="text-center text-sm font-semibold text-foreground">2</div>
                <div className="text-right text-sm text-muted-foreground flex items-center justify-end gap-1">
                  <span>—</span>
                  <span>Stable</span>
                </div>
              </div>

              {/* Negative */}
              <div className="grid grid-cols-3 gap-4 py-3 items-center">
                <div className="flex items-center gap-2">
                  <span className="text-base">😡</span>
                  <span className="text-sm text-foreground">Negative</span>
                </div>
                <div className="text-center text-sm font-semibold text-foreground">38</div>
                <div className="text-right text-sm text-destructive flex items-center justify-end gap-1">
                  <span>↘</span>
                  <span>Decreasing</span>
                </div>
              </div>
            </div>

            {/* Net Sentiment Index */}
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base">🤔</span>
                <span className="text-sm font-semibold text-foreground">Net Sentiment Index:</span>
                <span className="text-sm font-bold text-destructive">-20</span>
                <span className="text-xs text-muted-foreground">(Slightly Negative)</span>
              </div>
              <p className="text-xs text-muted-foreground ml-7">
                Driven by water & crowd concerns
              </p>
            </div>
          </div>
        </div>

        {/* AI-Generated Operational Insight */}
        <div className="bg-gradient-to-r from-primary/80 to-primary/60 rounded-xl p-6 shadow-sm mb-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg">💡</span>
            <h2 className="text-lg font-semibold text-primary-foreground">
              AI-Generated Operational Insight
            </h2>
          </div>

          <div className="space-y-3 text-primary-foreground">
            <div className="flex items-start gap-2">
              <span className="text-sm mt-0.5">✓</span>
              <p className="text-sm">
                <span className="font-semibold">Cleanliness satisfaction improving</span> due to new sanitation schedule.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-sm mt-0.5">⚡</span>
              <p className="text-sm">
                <span className="font-semibold">Power complaints consistent near Camp 1</span> — probable transformer issue.
              </p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-sm mt-0.5">💧</span>
              <p className="text-sm">
                Recommend a proactive inspection before next crowd surge window (3 PM-6 PM).
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Row - Trend Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Positive Feedback Trend */}
          <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">📈</span>
              <h2 className="text-base font-semibold text-success">
                Positive Feedback Trend
              </h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Past 6 hours</p>

            {/* Area Chart */}
            <div className="relative h-48">
              <svg width="100%" height="100%" viewBox="0 0 700 200" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="positiveGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="hsl(0, 0%, 30%)" stopOpacity="1" />
                    <stop offset="100%" stopColor="hsl(0, 0%, 90%)" stopOpacity="0.3" />
                  </linearGradient>
                </defs>
                <path
                  d="M 0 180 L 0 120 Q 116 100 233 90 Q 350 85 466 70 Q 583 60 700 50 L 700 180 Z"
                  fill="url(#positiveGradient)"
                />
                <path
                  d="M 0 120 Q 116 100 233 90 Q 350 85 466 70 Q 583 60 700 50"
                  fill="none"
                  stroke="hsl(0, 0%, 20%)"
                  strokeWidth="2"
                />
              </svg>

              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-muted-foreground">
                <span>24</span>
                <span>18</span>
                <span>12</span>
                <span>6</span>
                <span>0</span>
              </div>

              {/* X-axis labels */}
              <div className="absolute bottom-0 left-8 right-0 flex justify-between text-xs text-muted-foreground mt-2">
                <span>6h ago</span>
                <span>5h ago</span>
                <span>4h ago</span>
                <span>3h ago</span>
                <span>2h ago</span>
                <span>1h ago</span>
              </div>
            </div>
          </div>

          {/* Negative Feedback Trend */}
          <div className="bg-card rounded-xl p-6 shadow-sm border border-border">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-lg">📉</span>
              <h2 className="text-base font-semibold text-destructive">
                Negative Feedback Trend
              </h2>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Past 6 hours</p>

            {/* Area Chart */}
            <div className="relative h-48">
              <svg width="100%" height="100%" viewBox="0 0 700 200" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="negativeGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="hsl(0, 0%, 30%)" stopOpacity="1" />
                    <stop offset="100%" stopColor="hsl(0, 0%, 90%)" stopOpacity="0.3" />
                  </linearGradient>
                </defs>
                <path
                  d="M 0 180 L 0 40 Q 116 35 233 38 Q 350 40 466 42 Q 583 45 700 48 L 700 180 Z"
                  fill="url(#negativeGradient)"
                />
                <path
                  d="M 0 40 Q 116 35 233 38 Q 350 40 466 42 Q 583 45 700 48"
                  fill="none"
                  stroke="hsl(0, 0%, 20%)"
                  strokeWidth="2"
                />
              </svg>

              {/* Y-axis labels */}
              <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-muted-foreground">
                <span>60</span>
                <span>45</span>
                <span>30</span>
                <span>15</span>
                <span>0</span>
              </div>

              {/* X-axis labels */}
              <div className="absolute bottom-0 left-8 right-0 flex justify-between text-xs text-muted-foreground mt-2">
                <span>6h ago</span>
                <span>5h ago</span>
                <span>4h ago</span>
                <span>3h ago</span>
                <span>2h ago</span>
                <span>1h ago</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackIntelligence;
